import { colorSchemeOptionListAtom } from 'features/generator/contentFormSections/client/ContentDesignForm'
import { videoButtonListAtom } from 'features/generator/contentFormSections/VideoForm/ButtonsSection/ButtonsSection'
import { shareVideoAtom } from 'features/generator/contentFormSections/VideoForm/ShareVideoForm'
import { TVideo } from 'features/generator/contentFormSections/VideoForm/VideoFormModel'
import {
	videoDescriptionAtom,
	videoTitleAtom,
} from 'features/generator/contentFormSections/VideoForm/VideoInformationForm'
import { atom } from 'jotai'

import {
	primaryColorAtom,
	secondaryColorAtom,
	selectedColorSchemeAtom,
} from '@/content-from-sections/client'
import { TUploadedMedia } from '@/services'
import { getFileFromMedia, isOnServer } from '@/utils'

import {
	TQrColorScheme,
	welcomeImageAtom,
	welcomeImageFileAtom,
} from '../../contentFormSections'
import { videoListAtom } from '../../contentFormSections/VideoForm/VideoSection/VideoDetailsFields'
import { QR_TYPE } from '../../generatorTypes'

export type TVideoContent = {
	type: QR_TYPE.Video
	colors: Omit<TQrColorScheme, 'name'>
	isShareable: boolean
	info: {
		buttons: {
			title: string
			url: string | undefined
		}[]
		description: string
		title: string
	}
	videos: {
		name: string
		description: string
		source: {
			uploaded: TUploadedMedia
			url: string
		}
	}[]
	loaderImage: TUploadedMedia
}

// @ts-expect-error - Type mismatch is expected and handled at runtime
export const videoContentAtom = atom<TVideoContent, [TVideoContent], void>(
	(get) => ({
		type: QR_TYPE.Video,
		colors: {
			primary: get(primaryColorAtom),
			secondary: get(secondaryColorAtom),
		},
		info: {
			buttons: get(videoButtonListAtom),
			description: get(videoDescriptionAtom),
			title: get(videoTitleAtom),
		},
		videos: get(videoListAtom).map((video) => ({
			name: video.video.name,
			description: video.video.description,
			source: {
				uploaded: video.video.source.uploaded || null,
				url:
					video.video.source.uploaded && video.video.source.uploaded.publicId
						? null
						: video.video.source.url || null,
			},
		})),
		isShareable: get(shareVideoAtom),
		loaderImage:
			get(welcomeImageAtom) ||
			({
				publicId: '',
				resourceType: '',
				format: '',
				bytes: 0,
			} as TUploadedMedia),
	}),
	(get, set, content) => {
		// Color scheme
		const colorScheme = get(colorSchemeOptionListAtom).find(
			(scheme) =>
				scheme.primary === content.colors.primary &&
				scheme.secondary === content.colors.secondary,
		)

		set(selectedColorSchemeAtom, colorScheme?.name ?? '')
		set(primaryColorAtom, content.colors.primary)
		set(secondaryColorAtom, content.colors.secondary)

		set(videoTitleAtom, content.info.title)
		set(videoDescriptionAtom, content.info.description)
		set(videoButtonListAtom, content.info.buttons)

		set(shareVideoAtom, content.isShareable)

		const videos: TVideo[] = []
		content.videos.forEach((video) => {
			videos.push({
				video: {
					name: video.name,
					description: video.description || '',
					source: {
						// @ts-expect-error - TUploadedMedia type mismatch is expected
						uploaded: video.source.uploaded,
						url: video.source.url,
					},
				},
			})
		})
		set(videoListAtom, videos)

		set(welcomeImageAtom, content.loaderImage)
		if (!isOnServer) {
			setTimeout(async () => {
				try {
					const loaderFile = await getFileFromMedia(content.loaderImage)
					set(welcomeImageFileAtom, loaderFile)
				} catch (error) {
					console.error(error)
				}
			}, 0)
		}
	},
)
